
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useLevel3Training } from "@/hooks/level3/useLevel3Training";
import { useFavorites } from "@/hooks/useFavorites";
import { hasSavedExerciseData } from "@/hooks/level3/useAutoSave";

/**
 * Custom hook that handles Level 1 training-specific functionality
 */
export const useLevel1Training = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { refreshFavorites } = useFavorites();
  
  // Use the base Level3Training hook for shared functionality
  const trainingHook = useLevel3Training();
  
  const {
    scores,
    ballTendencies,
    selectedTest,
    currentPoint,
    currentAttempt,
    handleScoreChange,
    cancelLastEntry,
    handleFinish,
    handleSelectTest: baseHandleSelectTest,
    handleBackToTests: baseHandleBackToTests,
    getPointTotal,
    getGrandTotal
  } = trainingHook;
  
  // Override the base select test handler to update URL params
  const handleSelectTest = (testId: string) => {
    console.log('Test selected in Level1Training:', testId);
    baseHandleSelectTest(testId);
    setSearchParams({ test: testId });
  };
  
  // Override the back to tests handler to clear URL params
  const handleBackToTests = () => {
    console.log('Navigating back to test selection');
    baseHandleBackToTests();
    setSearchParams({});
  };
  
  // Load test from URL parameter on component mount - skip if saved data exists
  useEffect(() => {
    const { hasData } = hasSavedExerciseData();
    if (hasData) {
      console.log('Level1Training - Données sauvegardées détectées, skip URL loading');
      return;
    }
    
    console.log("Level1Training - Component mounted");
    const testParam = searchParams.get('test');
    console.log('URL test parameter:', testParam);
    
    if (testParam && !selectedTest) {
      console.log('Found test param, selecting test:', testParam);
      baseHandleSelectTest(testParam);
    }
  }, [searchParams, selectedTest, baseHandleSelectTest]);
  
  // Update URL when selected test changes
  useEffect(() => {
    if (selectedTest) {
      console.log('Updating URL with selected test:', selectedTest);
      setSearchParams({ test: selectedTest });
    } else {
      setSearchParams({});
    }
  }, [selectedTest, setSearchParams]);
  
  // Refresh favorites when component mounts
  useEffect(() => {
    refreshFavorites();
  }, [refreshFavorites]);
  
  return {
    scores,
    ballTendencies,
    selectedTest,
    currentPoint,
    currentAttempt,
    handleScoreChange,
    cancelLastEntry,
    handleFinish,
    handleSelectTest,
    handleBackToTests,
    getPointTotal,
    getGrandTotal
  };
};
